/**
 * Variant Picker - Hide Incompatible Options
 * 
 * FIXED: Uses global initialization pattern to prevent conflicts with Shopify's
 * variant picker JavaScript and DOM rerenders.
 * 
 * This script handles hiding incompatible variant options (specifically for Option 2)
 * based on the selected values of other options. It works with both button and dropdown
 * variant picker styles.
 */

(function () {
  'use strict';

  if (window.__VARIANT_PICKER_HIDE_INIT__) return;
  window.__VARIANT_PICKER_HIDE_INIT__ = true;

  const normalize = (val) => (val == null ? '' : String(val)).trim();

  function triggerVariantUpdate(element, picker) {
    if (!element) return;
    const changeEvent = new Event('change', { bubbles: true, cancelable: true });
    element.dispatchEvent(changeEvent);
    const inputEvent = new Event('input', { bubbles: true, cancelable: true });
    element.dispatchEvent(inputEvent);

    setTimeout(() => {
      const variantChangeEvent = new CustomEvent('variant:change', {
        bubbles: true,
        detail: { element: element, picker: picker }
      });
      picker.dispatchEvent(variantChangeEvent);

      const productForm = document.querySelector('form.shopify-product-form') ||
                         document.querySelector('form[id*="ProductForm"]') ||
                         document.querySelector('form[action*="/cart/add"]:not(.payment-terms)');

      if (productForm) {
        const formChangeEvent = new Event('change', { bubbles: true });
        productForm.dispatchEvent(formChangeEvent);
      }

      updateFormVariantId(picker);
    }, 50);
  }

  function getOptionCount(allOpts) {
    if (!allOpts || !allOpts.length) return 0;
    let count = 0;
    for (let idx = 0; idx < 3; idx++) {
      if (allOpts.some(opts => opts[idx] !== '')) count = idx + 1;
    }
    return count;
  }

  function updateFormVariantId(picker) {
    const allOpts = readVariants(picker);
    if (!allOpts || !allOpts.length) return;

    const selections = getSelections(picker);
    const optionCount = getOptionCount(allOpts);

    const variantsScript = picker.querySelector('script[type="application/json"][data-variants-json]');
    if (!variantsScript) return;

    try {
      const variants = JSON.parse(variantsScript.textContent || '[]');
      const matchingVariant = variants.find(v => {
        const variantOpts = [normalize(v.option1), normalize(v.option2), normalize(v.option3)];
        for (let idx = 0; idx < optionCount; idx++) {
          const selectionVal = normalize(selections[idx]);
          if (selectionVal && selectionVal !== variantOpts[idx]) return false;
        }
        return true;
      });

      if (matchingVariant && matchingVariant.id) {
        let productForm = document.querySelector('form.shopify-product-form') ||
                         document.querySelector('form[id*="ProductForm"]') ||
                         document.querySelector('form[action*="/cart/add"]:not(.payment-terms)');

        if (productForm) {
          let variantInput = productForm.querySelector('input[name="id"]') ||
                           productForm.querySelector('input[name="variant_id"]') ||
                           productForm.querySelector('[name="id"]');

          if (variantInput) {
            variantInput.value = matchingVariant.id;
          }
        }

        picker.setAttribute('data-selected-variant-id', matchingVariant.id);
      }
    } catch (e) {
      console.error('Error updating variant ID:', e);
    }
  }

  function isEnabled(picker) {
    const flag = (picker.getAttribute('data-hide-incompatible') || '').toLowerCase();
    return flag === 'true' || flag === '1' || flag === 'yes';
  }

  function readVariants(picker) {
    const script = picker.querySelector('script[type="application/json"][data-variants-json]');
    if (!script) return null;
    try {
      const variants = JSON.parse(script.textContent || '[]');
      return (variants || []).map(v => [
        normalize(v.option1),
        normalize(v.option2),
        normalize(v.option3)
      ]);
    } catch (e) {
      console.error('Error parsing variants JSON:', e);
      return null;
    }
  }

  function getSelections(picker) {
    const selections = [];
    picker.querySelectorAll('fieldset[data-fieldset-index]').forEach(fs => {
      const idx = parseInt(fs.getAttribute('data-fieldset-index'), 10);
      const checked = fs.querySelector('input[type="radio"]:checked');
      selections[idx] = normalize(checked && checked.value);
    });
    picker.querySelectorAll('select.variant-option__select').forEach(sel => {
      const anyOpt = sel.querySelector('option[data-input-id]');
      const inputId = anyOpt && anyOpt.getAttribute('data-input-id');
      const optIndex = inputId ? (parseInt(inputId.split('-')[0], 10) - 1) : null;
      if (optIndex != null && !Number.isNaN(optIndex)) {
        selections[optIndex] = normalize(sel.value);
      }
    });
    return selections;
  }

  function getAllowedValues(allOpts, selections, optionIndex) {
    const allowed = new Set();
    for (let i = 0; i < allOpts.length; i++) {
      const opts = allOpts[i];
      let ok = true;
      for (let j = 0; j < selections.length; j++) {
        if (j === optionIndex) continue;
        if (selections[j] && selections[j] !== opts[j]) {
          ok = false;
          break;
        }
      }
      if (ok) allowed.add(opts[optionIndex]);
    }
    return allowed;
  }

  // Directly measure and set the pill width from the live DOM, bypassing the
  // theme's own updateVariantPickerCss() entirely (its internal state goes stale
  // after its own async re-render). Guards against writing an unchanged value, since
  // that matters once this runs on a timer rather than only in direct response to a click.
  function syncPillWidth(picker) {
    picker.querySelectorAll('fieldset[data-fieldset-index]').forEach(fs => {
      const checked = fs.querySelector('input[type="radio"]:checked');
      if (!checked) return;
      const label = checked.closest('label.variant-option__button-label');
      if (!label || label.style.display === 'none') return;
      const width = label.offsetWidth;
      if (width > 0) {
        if (fs.style.getPropertyValue('--pill-width-current') !== width + 'px') {
          fs.style.setProperty('--pill-width-current', width + 'px');
        }
        if (fs.style.getPropertyValue('--pill-width-previous') !== width + 'px') {
          fs.style.setProperty('--pill-width-previous', width + 'px');
        }
      }
    });
  }

  // The theme's own async re-render (triggered by any variant change) can overwrite
  // our correct pill width with a stale one sometime after we've already set it
  // correctly. Rather than trying to precisely catch that one moment via MutationObserver
  // (which risks an infinite loop if it also watches attribute changes), just re-check
  // a few times on a short delay — cheap, and catches it regardless of exact timing.
  function scheduleDelayedPillSync(picker) {
    [300, 800, 1600].forEach(delay => setTimeout(() => syncPillWidth(picker), delay));
  }

  function applyToPicker(picker) {
    if (!picker || !isEnabled(picker)) return;

    const allOpts = readVariants(picker);
    if (!allOpts || !allOpts.length) return;

    const optionCount = getOptionCount(allOpts);
    if (optionCount < 2) {
      updateFormVariantId(picker);
      syncPillWidth(picker);
      scheduleDelayedPillSync(picker);
      return;
    }

    const selections = getSelections(picker);
    const allowed = getAllowedValues(allOpts, selections, 1);

    const supplyFs = picker.querySelector('fieldset[data-fieldset-index="1"]');
    if (supplyFs) {
      let currentlyChecked = null;
      let firstAvailable = null;
      let isCurrentHidden = false;

      supplyFs.querySelectorAll('label.variant-option__button-label').forEach(label => {
        const input = label.querySelector('input[type="radio"]');
        const val = normalize(input && input.value);
        const isAllowed = allowed.has(val);

        if (isAllowed) {
          label.style.removeProperty('display');
          if (!firstAvailable) firstAvailable = input;
        } else {
          label.style.setProperty('display', 'none', 'important');
        }

        if (input && input.checked) {
          currentlyChecked = input;
          if (!isAllowed) isCurrentHidden = true;
        }
      });

      if (isCurrentHidden && firstAvailable) {
        firstAvailable.checked = true;
        triggerVariantUpdate(firstAvailable, picker);
      }
    }

    picker.querySelectorAll('select.variant-option__select').forEach(sel => {
      const anyOpt = sel.querySelector('option[data-input-id]');
      const inputId = anyOpt && anyOpt.getAttribute('data-input-id');
      const optIndex = inputId ? (parseInt(inputId.split('-')[0], 10) - 1) : null;
      if (optIndex !== 1) return;

      let currentValue = normalize(sel.value);
      let isCurrentHidden = false;
      let firstAvailableValue = null;

      Array.from(sel.options).forEach(opt => {
        const v = normalize(opt.value);
        if (!v) return;

        const show = allowed.has(v);
        opt.hidden = !show;
        opt.disabled = !show;

        if (show && !firstAvailableValue) firstAvailableValue = v;
        if (v === currentValue && !show) isCurrentHidden = true;
      });

      if (isCurrentHidden && firstAvailableValue) {
        sel.value = firstAvailableValue;
        triggerVariantUpdate(sel, picker);
      }
    });

    updateFormVariantId(picker);
    syncPillWidth(picker);
    scheduleDelayedPillSync(picker);
  }

  function scheduleApply() {
    document.querySelectorAll('variant-picker').forEach(applyToPicker);
  }

  scheduleApply();
  document.addEventListener('change', () => setTimeout(scheduleApply, 0));
  document.addEventListener('click', () => setTimeout(scheduleApply, 0));

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', scheduleApply);
    document.addEventListener('shopify:section:reorder', scheduleApply);
    document.addEventListener('shopify:section:select', scheduleApply);
  }

  const observer = new MutationObserver(scheduleApply);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();